/**
 * URL Index Overview Page - Comprehensive Indexing Status & Action Items
 * 
 * Displays unified view combining:
 * 1. GSC/Bing Index Coverage
 * 2. Robots.txt Validation
 * 3. Action Items (derived from issues)
 * 
 * All data fetched from single url-index-overview endpoint.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Fade,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUrlIndexOverview } from '@/hooks/customer';
import { formatCompactNumber } from '@/services/customer/formatters';
import { ENGINE_COLORS_LOWER, SEMANTIC_COLORS } from '@/services/customer/constants';
import { MetricCard, DataTable, InlineError, MetricCardSkeleton, TableSkeleton } from '@/components/customer';
import type { UrlDetail } from '@/types/customer';


type EngineFilter = 'GSC' | 'BING';
type VerdictFilter = 'all' | 'indexed' | 'not_indexed' | 'error' | 'robots_blocked';
type TabView = 'overview' | 'issues' | 'actions';

const verdictFilterOptions: { value: VerdictFilter; label: string }[] = [
  { value: 'all', label: 'All URLs' },
  { value: 'indexed', label: 'Indexed' },
  { value: 'not_indexed', label: 'Not Indexed' },
  { value: 'error', label: 'Errors' },
  { value: 'robots_blocked', label: 'Robots.txt Blocked' },
];

function CustomerUrlCoveragePage() {
  const navigate = useNavigate();
  const [engine, setEngine] = useState<EngineFilter>('GSC');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('all');
  const [tabView, setTabView] = useState<TabView>('overview');

  const { data, isLoading, error, refetch } = useUrlIndexOverview({ engine });


  // Get engine-specific data
  const engineData = data?.data?.engines?.[engine];
  const urls = engineData?.urls ?? [];
  const robotsData = data?.data?.robotsValidations ?? [];

  // Merge GSC data with robots validation
  const enrichedUrls = useMemo(() => {
    return urls.map(url => {
      const robotsCheck = robotsData.find(r => r.url === url.url);
      return {
        ...url,
        robotsBlocked: robotsCheck?.isBlocked || false,
        robotsRule: robotsCheck?.matchedRule,
        robotsSeverity: robotsCheck?.severity,
      };
    });
  }, [urls, robotsData]);

  const filteredUrls = useMemo(() => {
    if (verdictFilter === 'all') return enrichedUrls;

    return enrichedUrls.filter((url) => {
      switch (verdictFilter) {
        case 'indexed':
          return url.verdict === 'PASS' || url.indexingState === 'INDEXING_ALLOWED';
        case 'not_indexed':
          return url.verdict === 'NEUTRAL' || url.verdict === 'FAIL';
        case 'error':
          return url.verdict === 'ERROR' || url.coverageState?.includes('error');
        case 'robots_blocked':
          return url.robotsBlocked;
        default:
          return true;
      }
    });
  }, [enrichedUrls, verdictFilter]);

  // Calculate comprehensive statistics
  const stats = useMemo(() => {
    const totalUrls = engineData?.totalUrls || 0;
    const indexed = engineData?.indexed || 0;
    const robotsBlocked = robotsData.filter(r => r.isBlocked).length;
    
    const issues = engineData?.issues || [];
    const redirectErrors = issues.find(i => i.reason?.toLowerCase().includes('redirect error'))?.count || 0;
    const unknownUrls = issues.find(i => i.reason?.toLowerCase().includes('unknown'))?.count || 0;
    const excludedUrls = issues.find(i => i.reason?.toLowerCase().includes('excluded'))?.count || 0;
    
    return {
      totalUrls,
      indexed,
      notIndexed: totalUrls - indexed,
      robotsBlocked,
      redirectErrors,
      unknownUrls,
      excludedUrls,
      indexCoverage: data?.data?.summary?.indexCoverage || 0,
    };
  }, [engineData, robotsData, data]);


  const columns = [
    {
      field: 'url',
      headerName: 'URL',
      flex: 2,
      render: (item: any) => (
        <Box>
          <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
            {item.url}
          </Typography>
          {item.robotsBlocked && (
            <Chip
              icon={<BlockIcon sx={{ fontSize: 14 }} />}
              label="Robots.txt Blocked"
              size="small"
              sx={{
                mt: 0.5,
                bgcolor: '#d32f2f',
                color: 'white',
                fontSize: '0.7rem',
                height: 20,
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      render: (item: any) => {
        // Priority: Show most critical issue
        if (item.robotsBlocked) {
          return (
            <Chip
              icon={<BlockIcon />}
              label="Blocked by robots.txt"
              size="small"
              sx={{ bgcolor: '#d32f2f', color: 'white', fontWeight: 500 }}
            />
          );
        }
        if (item.coverageState?.toLowerCase().includes('redirect error')) {
          return (
            <Chip
              icon={<ErrorIcon />}
              label="Redirect Error"
              size="small"
              sx={{ bgcolor: SEMANTIC_COLORS.error, color: 'white', fontWeight: 500 }}
            />
          );
        }
        if (item.coverageState?.toLowerCase().includes('submitted and indexed')) {
          return (
            <Chip
              icon={<CheckCircleIcon />}
              label="Indexed"
              size="small"
              sx={{ bgcolor: SEMANTIC_COLORS.success, color: 'white', fontWeight: 500 }}
            />
          );
        }
        if (item.coverageState?.toLowerCase().includes('unknown')) {
          return (
            <Chip
              icon={<WarningIcon />}
              label="Unknown to Google"
              size="small"
              sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 500 }}
            />
          );
        }
        return (
          <Typography variant="body2" color="text.secondary">
            {item.coverageState}
          </Typography>
        );
      },
    },
    {
      field: 'lastCrawlAt',
      headerName: 'Last Crawled',
      flex: 0.75,
      render: (item: any) => (
        <Typography variant="body2" color="text.secondary">
          {item.lastCrawlAt ? new Date(item.lastCrawlAt).toLocaleDateString() : 'Never'}
        </Typography>
      ),
    },
    {
      field: 'robotsStatus',
      headerName: 'Robots.txt Status',
      flex: 0.8,
      render: (item: any) => {
        if (item.robotsBlocked) {
          return (
            <Chip
              label="Blocked"
              size="small"
              sx={{ 
                bgcolor: '#d32f2f', 
                color: 'white', 
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          );
        }
        return (
          <Chip
            label="Allowed"
            size="small"
            sx={{ 
              bgcolor: SEMANTIC_COLORS.success, 
              color: 'white', 
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Recommended Action',
      flex: 1.5,
      render: (item: any) => {
        if (item.robotsBlocked && item.robotsRule) {
          return (
            <Alert severity="error" sx={{ py: 0, px: 1 }}>
              <Typography variant="caption">
                Remove rule: <code>{item.robotsRule}</code>
              </Typography>
            </Alert>
          );
        }
        if (item.coverageState?.toLowerCase().includes('redirect error')) {
          return (
            <Alert severity="error" sx={{ py: 0, px: 1 }}>
              <Typography variant="caption">Fix redirect chain or broken redirect</Typography>
            </Alert>
          );
        }
        if (item.coverageState?.toLowerCase().includes('unknown')) {
          return (
            <Alert severity="warning" sx={{ py: 0, px: 1 }}>
              <Typography variant="caption">Submit URL to GSC or add to sitemap</Typography>
            </Alert>
          );
        }
        if (item.verdict === 'PASS') {
          return (
            <Typography variant="caption" color="success.main">
              ✓ No action needed
            </Typography>
          );
        }
        return <Typography variant="caption" color="text.disabled">—</Typography>;
      },
    },
  ];


  if (isLoading) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={350} height={24} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={6} md={4} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
        <TableSkeleton />
      </Box>
    );
  }

  const summary = data?.data?.summary;

  return (
    <Fade in timeout={300}>
      <Box sx={{ maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 }, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/early-stage')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" gutterBottom>
            Index Coverage & Action Items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive indexing status combining GSC data, robots.txt validation, and actionable recommendations
          </Typography>
        </Box>

        {error && (
          <InlineError error={error} title="Failed to load URL index data" onRetry={refetch} />
        )}

        {/* Engine Selector */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={engine}
            exclusive
            onChange={(_, newEngine) => newEngine && setEngine(newEngine)}
            aria-label="search engine"
          >
            <ToggleButton
              value="GSC"
              sx={{
                '&.Mui-selected': {
                  backgroundColor: ENGINE_COLORS_LOWER.google,
                  color: 'white',
                  '&:hover': { backgroundColor: ENGINE_COLORS_LOWER.google },
                },
              }}
            >
              Google
            </ToggleButton>
            <ToggleButton
              value="BING"
              sx={{
                '&.Mui-selected': {
                  backgroundColor: ENGINE_COLORS_LOWER.bing,
                  color: 'white',
                  '&:hover': { backgroundColor: ENGINE_COLORS_LOWER.bing },
                },
              }}
            >
              Bing
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Comprehensive Statistics Grid - 6 Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: SEMANTIC_COLORS.success }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Indexed
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: SEMANTIC_COLORS.success, fontWeight: 600 }}>
                  {stats.indexed}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.indexCoverage.toFixed(1)}% of total
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningIcon sx={{ color: '#ff9800' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Unknown to Google
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#ff9800', fontWeight: 600 }}>
                  {stats.unknownUrls}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Not discovered yet
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ErrorIcon sx={{ color: SEMANTIC_COLORS.error }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Redirect Errors
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: SEMANTIC_COLORS.error, fontWeight: 600 }}>
                  {stats.redirectErrors}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Fix redirects
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BlockIcon sx={{ color: '#d32f2f' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Robots.txt Blocked
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                  {stats.robotsBlocked}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sitemap conflicts
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <VisibilityIcon sx={{ color: '#9e9e9e' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Excluded
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#9e9e9e', fontWeight: 600 }}>
                  {stats.excludedUrls}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Not in index
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BuildIcon sx={{ color: SEMANTIC_COLORS.info }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total URLs
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: SEMANTIC_COLORS.info, fontWeight: 600 }}>
                  {stats.totalUrls}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tracked URLs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tab Navigation */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tabView}
            onChange={(_, newValue) => setTabView(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Overview" value="overview" />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Issues by Type
                  {(stats.redirectErrors + stats.unknownUrls + stats.robotsBlocked) > 0 && (
                    <Chip
                      label={stats.redirectErrors + stats.unknownUrls + stats.robotsBlocked}
                      size="small"
                      color="error"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              } 
              value="issues" 
            />
            <Tab label="Action Items" value="actions" />
          </Tabs>

          <CardContent>
            {/* Overview Tab */}
            {tabView === 'overview' && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Index Coverage Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Coverage Breakdown
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 2 }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: SEMANTIC_COLORS.success }} />
                            <Typography variant="body2">Indexed</Typography>
                          </Box>
                          <Typography variant="h5" sx={{ ml: 2.5, color: SEMANTIC_COLORS.success }}>
                            {stats.indexed}
                          </Typography>
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
                            <Typography variant="body2">Not Indexed</Typography>
                          </Box>
                          <Typography variant="h5" sx={{ ml: 2.5, color: '#ff9800' }}>
                            {stats.notIndexed}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Critical Issues
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {stats.redirectErrors > 0 && (
                          <Alert severity="error" sx={{ mb: 1 }}>
                            <AlertTitle>Redirect Errors: {stats.redirectErrors}</AlertTitle>
                            Fix broken or incorrect redirects immediately
                          </Alert>
                        )}
                        {stats.robotsBlocked > 0 && (
                          <Alert severity="error" sx={{ mb: 1 }}>
                            <AlertTitle>Robots.txt Conflicts: {stats.robotsBlocked}</AlertTitle>
                            URLs in sitemap but blocked by robots.txt
                          </Alert>
                        )}
                        {stats.redirectErrors === 0 && stats.robotsBlocked === 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: SEMANTIC_COLORS.success }}>
                            <CheckCircleIcon />
                            <Typography>No critical issues found</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Issues Tab */}
            {tabView === 'issues' && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Issues by Type
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {engineData?.issues && engineData.issues.length > 0 ? (
                  <Grid container spacing={2}>
                    {engineData.issues.map((issue, idx) => {
                      const isError = issue.severity === 'HIGH';
                      const isWarning = issue.severity === 'MEDIUM';
                      return (
                        <Grid item xs={12} md={6} key={idx}>
                          <Alert 
                            severity={isError ? 'error' : isWarning ? 'warning' : 'info'}
                            icon={isError ? <ErrorIcon /> : isWarning ? <WarningIcon /> : undefined}
                          >
                            <AlertTitle>
                              {issue.reason} ({issue.count} URLs)
                            </AlertTitle>
                            <Typography variant="caption">
                              Severity: {issue.severity}
                            </Typography>
                          </Alert>
                        </Grid>
                      );
                    })}
                    {stats.robotsBlocked > 0 && (
                      <Grid item xs={12} md={6}>
                        <Alert severity="error" icon={<BlockIcon />}>
                          <AlertTitle>
                            Robots.txt Blocked ({stats.robotsBlocked} URLs)
                          </AlertTitle>
                          <Typography variant="caption">
                            URLs in sitemap but blocked by robots.txt rules
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 60, color: SEMANTIC_COLORS.success, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No issues detected
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Actions Tab */}
            {tabView === 'actions' && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Recommended Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {stats.robotsBlocked > 0 && (
                    <Alert severity="error" icon={<BuildIcon />}>
                      <AlertTitle>High Priority: Fix Robots.txt Conflicts ({stats.robotsBlocked} URLs)</AlertTitle>
                      <Typography variant="body2">
                        • Review robots.txt Disallow rules<br />
                        • Remove blocking rules for URLs you want indexed<br />
                        • Or remove blocked URLs from sitemap
                      </Typography>
                    </Alert>
                  )}
                  {stats.redirectErrors > 0 && (
                    <Alert severity="error" icon={<BuildIcon />}>
                      <AlertTitle>High Priority: Fix Redirect Errors ({stats.redirectErrors} URLs)</AlertTitle>
                      <Typography variant="body2">
                        • Check for redirect chains (A → B → C)<br />
                        • Fix broken redirects (404 destinations)<br />
                        • Ensure redirects use 301 (permanent) status codes
                      </Typography>
                    </Alert>
                  )}
                  {stats.unknownUrls > 0 && (
                    <Alert severity="warning" icon={<BuildIcon />}>
                      <AlertTitle>Medium Priority: Submit Unknown URLs ({stats.unknownUrls} URLs)</AlertTitle>
                      <Typography variant="body2">
                        • Add URLs to sitemap.xml<br />
                        • Submit sitemap to Google Search Console<br />
                        • Or manually request indexing via GSC
                      </Typography>
                    </Alert>
                  )}
                  {stats.excludedUrls > 0 && (
                    <Alert severity="info" icon={<BuildIcon />}>
                      <AlertTitle>Low Priority: Review Excluded URLs ({stats.excludedUrls})</AlertTitle>
                      <Typography variant="body2">
                        • Check if exclusions are intentional<br />
                        • Review noindex tags and meta robots<br />
                        • Consider if these pages should be indexed
                      </Typography>
                    </Alert>
                  )}
                  {stats.robotsBlocked === 0 && stats.redirectErrors === 0 && stats.unknownUrls === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CheckCircleIcon sx={{ fontSize: 60, color: SEMANTIC_COLORS.success, mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No actions required
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your site's indexing configuration is optimal
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Filter Controls */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2">Filter URLs:</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={verdictFilter}
              label="Status Filter"
              onChange={(e) => setVerdictFilter(e.target.value as VerdictFilter)}
            >
              {verdictFilterOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredUrls.length} of {enrichedUrls.length} URLs
          </Typography>
        </Box>

        {/* URL Details Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              URL Details
            </Typography>
            <DataTable
              data={filteredUrls}
              columns={columns}
              searchKey="url"
              searchPlaceholder="Search URLs..."
            />
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
}

export default CustomerUrlCoveragePage;
